/**
 * IDB Manifest
 *
 * Tracks all IndexedDB databases created by River in a dedicated manifest
 * database. Replaces the dependency on indexedDB.databases() (Chrome-only)
 * with a cross-browser solution for enumeration and cleanup.
 *
 * Every service that creates or destroys an IDB database should call
 * register() / unregister() via this module.
 */

import { IDBKeyType, buildIDBKey } from './idbKeyManager';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('IDB_MANIFEST');

const MANIFEST_DB_NAME = buildIDBKey('manifest', '');
const MANIFEST_DB_VERSION = 1;
const STORE_NAME = 'databases';

export interface ManifestEntry {
  key: string;            // IDB database name (primary key)
  type: IDBKeyType;
  entityId: string;       // The entity/room ID this DB belongs to
  createdAt: number;
  lastAccessedAt: number;
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

class IDBManifest {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  private async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(MANIFEST_DB_NAME, MANIFEST_DB_VERSION);

      request.onerror = () => {
        log.error('[IDBManifest] Failed to open manifest DB:', request.error);
        this.initPromise = null;
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;

        this.db.onclose = () => {
          log.warn('[IDBManifest] Manifest DB closed unexpectedly');
          this.db = null;
        };

        this.db.onversionchange = () => {
          log.warn('[IDBManifest] Manifest DB version changed, closing');
          this.db?.close();
          this.db = null;
        };

        this.initPromise = null;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('by_entity', 'entityId', { unique: false });
          store.createIndex('by_type', 'type', { unique: false });
          store.createIndex('by_last_accessed', 'lastAccessedAt', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  private async ensureDb(): Promise<IDBDatabase | null> {
    await this.init();
    return this.db;
  }

  /**
   * Register a newly created IDB database in the manifest.
   */
  async register(key: string, type: IDBKeyType, entityId: string): Promise<void> {
    try {
      const db = await this.ensureDb();
      if (!db) return;

      const now = Date.now();
      const entry: ManifestEntry = {
        key,
        type,
        entityId,
        createdAt: now,
        lastAccessedAt: now,
      };

      const tx = db.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await idbRequest(store.put(entry));

      log.info('[IDBManifest] Registered database', { key, type, entityId });
    } catch (error) {
      log.error('[IDBManifest] Error registering database:', error);
    }
  }

  /**
   * Remove a database entry from the manifest (call after deleting the actual DB).
   */
  async unregister(key: string): Promise<void> {
    try {
      const db = await this.ensureDb();
      if (!db) return;

      const tx = db.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await idbRequest(store.delete(key));

      log.info('[IDBManifest] Unregistered database', { key });
    } catch (error) {
      log.error('[IDBManifest] Error unregistering database:', error);
    }
  }

  /**
   * Update the lastAccessedAt timestamp for an existing entry.
   * Uses a cursor to read+write in a single request, avoiding the IDB
   * transaction auto-commit issue that occurs when awaiting between
   * separate get() and put() calls on the same transaction.
   */
  async touchLastAccessed(key: string): Promise<void> {
    try {
      const db = await this.ensureDb();
      if (!db) return;

      const tx = db.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const cursorReq = store.openCursor(key);
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (cursor) {
            const entry = cursor.value as ManifestEntry;
            entry.lastAccessedAt = Date.now();
            const updateReq = cursor.update(entry);
            updateReq.onsuccess = () => resolve();
            updateReq.onerror = () => reject(updateReq.error);
          } else {
            resolve();
          }
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      });
    } catch (error) {
      log.error('[IDBManifest] Error touching lastAccessedAt:', error);
    }
  }

  /**
   * Get all tracked databases.
   */
  async getAllEntries(): Promise<ManifestEntry[]> {
    try {
      const db = await this.ensureDb();
      if (!db) return [];

      const tx = db.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);
      return await idbRequest<ManifestEntry[]>(store.getAll());
    } catch (error) {
      log.error('[IDBManifest] Error getting all entries:', error);
      return [];
    }
  }

  /**
   * Get all tracked databases for a specific entity ID.
   * This is the cross-browser replacement for scanning indexedDB.databases().
   */
  async getEntriesForEntity(entityId: string): Promise<ManifestEntry[]> {
    try {
      const db = await this.ensureDb();
      if (!db) return [];

      const tx = db.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('by_entity');
      return await idbRequest<ManifestEntry[]>(index.getAll(entityId));
    } catch (error) {
      log.error('[IDBManifest] Error getting entries for entity:', error);
      return [];
    }
  }

  /**
   * Get entries sorted by lastAccessedAt (oldest first), optionally filtered by type.
   */
  async getEntriesByAge(types?: IDBKeyType[]): Promise<ManifestEntry[]> {
    try {
      const entries = await this.getAllEntries();
      const filtered = types
        ? entries.filter(e => types.includes(e.type))
        : entries;
      return filtered.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
    } catch (error) {
      log.error('[IDBManifest] Error getting entries by age:', error);
      return [];
    }
  }

  /**
   * Get current storage estimate (where supported).
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number; percentUsed: number }> {
    if (navigator.storage?.estimate) {
      const { usage = 0, quota = 0 } = await navigator.storage.estimate();
      return { usage, quota, percentUsed: quota > 0 ? (usage / quota) * 100 : 0 };
    }
    return { usage: 0, quota: 0, percentUsed: 0 };
  }

  /**
   * Delete the oldest N evictable databases (entity-sync, room-crdt, file-editor)
   * and unregister them from the manifest.
   * Returns the keys that were evicted.
   */
  async evictLRU(count: number): Promise<string[]> {
    const evictableTypes: IDBKeyType[] = ['entity-sync', 'room-crdt', 'file-editor'];
    const entries = await this.getEntriesByAge(evictableTypes);
    const toEvict = entries.slice(0, count);
    const evictedKeys: string[] = [];

    for (const entry of toEvict) {
      try {
        await new Promise<void>((resolve) => {
          const req = indexedDB.deleteDatabase(entry.key);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        });
        await this.unregister(entry.key);
        evictedKeys.push(entry.key);
      } catch {
        // Non-fatal — continue evicting others
      }
    }

    if (evictedKeys.length > 0) {
      log.info('[IDBManifest] Evicted LRU databases', { count: evictedKeys.length });
    }
    return evictedKeys;
  }
}

export const idbManifest = new IDBManifest();
